import i18n from '@/i18n';

/**
 * Get the Intl locale corresponding to the active i18n language.
 * en → en-GB (European English: 24h time, DD/MM/YYYY)
 * de → de-DE (German: 24h time, DD.MM.YYYY)
 */
function getIntlLocale(): string {
  const lang = i18n.language?.substring(0, 2) || 'en';
  return lang === 'de' ? 'de-DE' : 'en-GB';
}

/**
 * Format a date for display, respecting the active locale.
 * Returns '-' for invalid or missing dates.
 */
export function formatDate(date: Date | string | null | undefined): string {
  if (!date) return '-';

  const d = typeof date === 'string' ? new Date(date) : date;

  // Check for invalid date
  if (isNaN(d.getTime())) return '-';

  return d.toLocaleDateString(getIntlLocale(), {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format a date as ISO string for API calls.
 */
export function formatDateISO(date: Date): string {
  return date.toISOString();
}

/**
 * Format a number as currency in EUR, respecting the active locale.
 * @param amount Amount in full currency units (e.g., 4.99)
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat(getIntlLocale(), {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
}

/**
 * Format cents as currency in EUR.
 * @param cents Amount in cents (e.g., 499 for €4.99)
 */
export function formatCents(cents: number | null | undefined): string {
  if (cents == null || typeof cents !== 'number' || isNaN(cents)) return '-';
  return formatCurrency(cents / 100);
}

/**
 * Truncate a string to a maximum length.
 */
export function truncate(str: string, maxLength: number): string {
  if (str.length <= maxLength) return str;
  return str.slice(0, maxLength - 3) + '...';
}
